/**
 * Authorities — case-file workflow hub.
 *
 * Single tab that replaces the dedicated "Searches" and "Post-Completion"
 * tabs and folds them under a visual workflow diagram organised by the
 * external authority a step talks to (Onboarding, HMLR, Legalito,
 * Post-Completion). Each authority tile carries:
 *   - its own icon + headline,
 *   - quick-action chips for the most common sub-flows,
 *   - a "Open" affordance that loads the underlying component (SearchesTab
 *     for HMLR, PostCompletionTab for Post-Completion) inline below the
 *     workflow strip so the existing UI is preserved verbatim.
 *
 * Phase 1 wires HMLR + Post-Completion straight through to the existing
 * components. Onboarding + Legalito render scoped "coming soon" panels
 * with the planned feature list, so the shape is visible ahead of build.
 */
import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import API from "@/lib/api";
import {
  House,
  IdentificationCard,
  Stamp,
  ChatCircleText,
  ArrowRight,
  CaretDown,
  Wrench,
  CheckCircle,
  FileText,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import PostCompletionTab from "@/components/app/PostCompletionTab";
import SearchesTab from "@/components/app/SearchesTab";
import AmlTab from "@/components/app/AmlTab";
import OnboardingStatusPanel from "@/components/app/OnboardingStatusPanel";
import OnboardingPackDialog from "@/components/app/onboarding/OnboardingPackDialog";
import ContractPackDialog from "@/components/app/ContractPackDialog";
import LegalitoPanel from "@/components/app/authorities/LegalitoPanel";

// ---- Tile metadata ------------------------------------------------------

const TILES = [
  {
    id: "onboarding",
    title: "Onboarding",
    subtitle: "ID + AML + PoA (Didit)",
    icon: IdentificationCard,
    tone: "violet",
    actions: [
      { id: "didit_verify", label: "Verify identity (Didit)" },
      { id: "client_care_pack", label: "Send Client Care Pack" },
    ],
    status: "live",
  },
  {
    id: "hmlr",
    title: "HMLR",
    subtitle: "Searches & title documents",
    icon: House,
    tone: "blue",
    actions: [
      { id: "office_copies", label: "Download title documents" },
      { id: "priority", label: "OS1 / OS2 priority search" },
      { id: "bankruptcy", label: "K15 / K16 bankruptcy search" },
    ],
    status: "live",
  },
  {
    id: "legalito",
    title: "Legalito",
    subtitle: "Enquiries tracker",
    icon: ChatCircleText,
    tone: "amber",
    actions: [
      { id: "raise_enquiry", label: "Raise enquiry" },
      { id: "track_responses", label: "Track responses" },
    ],
    status: "live",
  },
  {
    id: "post_completion",
    title: "Post-Completion",
    subtitle: "HMRC, HMLR & Companies House",
    icon: Stamp,
    tone: "emerald",
    actions: [
      { id: "submit_hmrc", label: "Submit SDLT to HMRC" },
      { id: "submit_hmlr", label: "Submit AP1 to HMLR" },
      { id: "submit_ch", label: "Submit MR01 to Companies House" },
    ],
    status: "live",
  },
];

const TONE_CLASSES = {
  violet: { ring: "ring-violet-200", border: "border-violet-200", bg: "bg-violet-50", icon: "text-violet-700", accent: "border-violet-300 bg-violet-100/60" },
  blue: { ring: "ring-[#1462a0]/30", border: "border-[#1462a0]/30", bg: "bg-[#1462a0]/5", icon: "text-[#1462a0]", accent: "border-[#1462a0]/30 bg-[#1462a0]/10" },
  amber: { ring: "ring-amber-200", border: "border-amber-200", bg: "bg-amber-50", icon: "text-amber-700", accent: "border-amber-300 bg-amber-100/60" },
  emerald: { ring: "ring-emerald-200", border: "border-emerald-200", bg: "bg-emerald-50", icon: "text-emerald-700", accent: "border-emerald-300 bg-emerald-100/60" },
};

// ---- Progress strip ----------------------------------------------------
//
// One-glance counts across the four authorities. Pulls from the three
// existing endpoints in parallel so we don't need a new backend route.
// Legalito is a placeholder (— / —) until Phase 4.

function useAuthoritiesSummary(caseId) {
  const [summary, setSummary] = useState({ loading: true });
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [aml, searches, pc] = await Promise.all([
          API.get(`/cases/${caseId}/aml-checks`).catch(() => ({ data: null })),
          API.get(`/cases/${caseId}/searches`).catch(() => ({ data: null })),
          API.get(`/cases/${caseId}/post-completion`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const amlBy = aml.data?.by_client || {};
        const amlValues = Object.values(amlBy);
        const amlClear = amlValues.filter((c) => (c.verdict || "").toLowerCase() === "clear").length;
        const sSum = searches.data?.summary || {};
        const pcSum = pc.data?.summary || {};
        setSummary({
          loading: false,
          aml: { complete: amlClear, total: amlValues.length, state: aml.data?.case_summary?.state || "none" },
          searches: { complete: sSum.completed || 0, total: sSum.total || 0, priority: sSum.priority_active || 0 },
          post_completion: {
            complete: pcSum.completed || 0,
            // post-completion endpoint already returns `total`; fall
            // back to the sum so this code keeps working if the field
            // is renamed.
            total: pcSum.total ?? ((pcSum.pending || 0) + (pcSum.completed || 0) + (pcSum.overdue || 0)),
            overdue: pcSum.overdue || 0,
          },
        });
      } catch (e) {
        if (!cancelled) setSummary({ loading: false, error: true });
      }
    }
    load();
    return () => { cancelled = true; };
  }, [caseId]);
  return summary;
}

function ProgressPill({ label, complete = 0, total = 0, tone = "slate", extra, testid }) {
  const pct = total > 0 ? Math.round((complete / total) * 100) : 0;
  const toneClasses = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    blue: "border-[#1462a0]/30 bg-[#1462a0]/5 text-[#1462a0]",
    violet: "border-violet-200 bg-violet-50 text-violet-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div className={`flex-1 min-w-[170px] border rounded-lg p-3 ${toneClasses}`} data-testid={testid}>
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide opacity-80">
        <span>{label}</span>
        {extra && <span className="text-[10px] font-normal opacity-75">{extra}</span>}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-xl font-semibold text-slate-900">{complete}</span>
        <span className="text-xs text-slate-500">/ {total}</span>
      </div>
      <div className="mt-1.5 h-1 bg-white/60 rounded overflow-hidden">
        <div className="h-full bg-current opacity-50" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ProgressStrip({ caseId }) {
  const s = useAuthoritiesSummary(caseId);
  if (s.loading) {
    return <div className="text-xs text-slate-400 px-1">Loading authority progress…</div>;
  }
  if (s.error) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3" data-testid="authorities-progress-strip">
      <ProgressPill
        label="Onboarding — ID/AML"
        complete={s.aml?.complete}
        total={s.aml?.total}
        tone={s.aml?.state === "fail" ? "rose" : s.aml?.state === "warn" ? "amber" : s.aml?.complete && s.aml?.complete === s.aml?.total ? "emerald" : "violet"}
        testid="progress-onboarding"
      />
      <ProgressPill
        label="HMLR — Searches"
        complete={s.searches?.complete}
        total={s.searches?.total}
        tone="blue"
        extra={s.searches?.priority ? `${s.searches.priority} priority live` : null}
        testid="progress-hmlr"
      />
      <ProgressPill
        label="Legalito — Enquiries"
        complete={0}
        total={0}
        tone="amber"
        extra="Phase 4"
        testid="progress-legalito"
      />
      <ProgressPill
        label="Post-Completion — Submissions"
        complete={s.post_completion?.complete}
        total={s.post_completion?.total}
        tone={s.post_completion?.overdue ? "rose" : "emerald"}
        extra={s.post_completion?.overdue ? `${s.post_completion.overdue} overdue` : null}
        testid="progress-post-completion"
      />
    </div>
  );
}

// ---- Connector arrow between tiles -------------------------------------

function FlowArrow() {
  return (
    <div className="hidden lg:flex items-center justify-center text-slate-300 px-1 shrink-0" aria-hidden="true">
      <ArrowRight weight="bold" className="w-5 h-5" />
    </div>
  );
}

// ---- Single workflow tile ----------------------------------------------

function WorkflowTile({ tile, active, onOpen, onAction }) {
  const Icon = tile.icon;
  const tone = TONE_CLASSES[tile.tone] || TONE_CLASSES.blue;
  return (
    <button
      onClick={() => onOpen(tile.id)}
      className={`relative flex-1 min-w-[200px] text-left rounded-xl border ${tone.ring} ring-1 ${
        active ? `${tone.bg} border-transparent shadow-sm` : "bg-white border-slate-200 hover:bg-slate-50"
      } p-4 transition`}
      data-testid={`authority-tile-${tile.id}`}
    >
      {/* status pip */}
      <div className="absolute top-3 right-3">
        {tile.status === "live" ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
            <CheckCircle weight="fill" className="w-3 h-3" /> Live
          </span>
        ) : (
          <span className="text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
            <Wrench weight="fill" className="w-3 h-3" /> Soon
          </span>
        )}
      </div>

      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${tone.bg} ${tone.icon} mb-3`}>
        <Icon weight="duotone" className="w-7 h-7" />
      </div>
      <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{tile.title}</h3>
      <p className="text-xs text-slate-500 mt-0.5">{tile.subtitle}</p>

      {/* sub-actions — visible always (touch-friendly, no hover-only) */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {tile.actions.map((a) => (
          <span
            key={a.id}
            onClick={(e) => {
              e.stopPropagation();
              onAction(tile.id, a.id);
            }}
            className={`text-[11px] px-2 py-1 rounded-md border ${tone.accent} text-slate-700 hover:bg-white cursor-pointer transition`}
            data-testid={`authority-action-${tile.id}-${a.id}`}
          >
            {a.label}
          </span>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
        <span>{active ? "Showing below" : "Click to open"}</span>
        <CaretDown weight="bold" className={`w-3.5 h-3.5 transition-transform ${active ? "rotate-180" : ""}`} />
      </div>
    </button>
  );
}

// ---- Coming-soon panel --------------------------------------------------

function ComingSoonPanel({ tile }) {
  const Icon = tile.icon;
  const tone = TONE_CLASSES[tile.tone] || TONE_CLASSES.blue;
  const COPY = {
    onboarding: {
      headline: "Client Care Pack — Phase 2",
      bullets: [
        "Populated Client Care Letter, T&Cs, Source-of-Funds + Personal-Info questionnaires, TA6 / TA7 / TA10 for sale matters",
        "Magic-link client portal — 4-step electronic flow: 1) pay money on account · 2) sign client care letter + T&Cs · 3) complete questionnaires · 4) sign TA protocol forms",
        "DocuSign-or-equivalent e-signing on every step, with each item signed individually so the case file builds itself as the client progresses",
        "Send as a pack OR individually (e.g. fire just the source-of-funds questionnaire later if circumstances change)",
      ],
    },
    legalito: {
      headline: "Legalito enquiries tracker — Phase 4",
      bullets: [
        "Sync raised enquiries from Legalito into the case file",
        "Track response status (received / outstanding / re-raised) with SLA timers",
        "Auto-attach Legalito's enquiry PDF + response thread into the Documents tab",
        "Firm-wide enquiries dashboard mirroring the existing Post-Completion dashboard",
      ],
    },
  };
  const meta = COPY[tile.id] || { headline: "Coming soon", bullets: [] };
  return (
    <div className={`rounded-lg border-2 border-dashed ${tone.border} ${tone.bg} p-6`} data-testid={`authority-soon-${tile.id}`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${tone.icon} bg-white shrink-0`}>
          <Icon weight="duotone" className="w-7 h-7" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{meta.headline}</h3>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
            {meta.bullets.map((b, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-slate-400 mt-0.5">▸</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---- Main component -----------------------------------------------------

export default function AuthoritiesTab({ caseId, caseData, isSolicitor }) {
  const [active, setActive] = useState(null);
  // Each sub-action sets a "trigger" that the child component
  // (SearchesTab / PostCompletionTab) can pick up to auto-open the right
  // dialog. We bump a counter so the same action can fire twice in a row.
  const [trigger, setTrigger] = useState({ key: null, ts: 0 });
  const [packOpen, setPackOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const triggerRef = useRef(null);

  const handleOpen = (id) => {
    setActive((curr) => (curr === id ? null : id));
    // scroll the loaded panel into view a beat after render
    setTimeout(() => { triggerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 100);
  };

  const handleAction = (tileId, actionId) => {
    // Client Care Pack is a dialog-only flow — doesn't need the panel.
    if (tileId === "onboarding" && actionId === "client_care_pack") {
      setPackOpen(true);
      return;
    }
    // Open the tile and queue a trigger the child will consume.
    setActive(tileId);
    setTrigger({ key: `${tileId}:${actionId}`, ts: Date.now() });
    setTimeout(() => { triggerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 100);
  };

  const activeTile = TILES.find((t) => t.id === active);

  return (
    <div className="space-y-5" data-testid="authorities-tab">
      {/* Workflow strip */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Authorities workflow
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Everything that talks to an external authority — onboarding, HMLR, Legalito and post-completion submissions — in one orchestrated flow.
            </p>
          </div>
          {isSolicitor && (
            <Button
              onClick={() => setContractOpen(true)}
              className="bg-[#1462a0] hover:bg-[#10507f] text-white h-9 shrink-0"
              data-testid="open-contract-pack-btn"
            >
              <FileText weight="bold" className="w-4 h-4 mr-1.5" /> Contract Pack
            </Button>
          )}
        </div>
        <ProgressStrip caseId={caseId} />
        <div className="flex flex-col lg:flex-row items-stretch gap-3 mt-4" data-testid="authorities-flow">
          {TILES.map((tile, i) => (
            <Fragment key={tile.id}>
              <WorkflowTile
                tile={tile}
                active={active === tile.id}
                onOpen={handleOpen}
                onAction={handleAction}
              />
              {i < TILES.length - 1 && <FlowArrow />}
            </Fragment>
          ))}
        </div>
      </div>

      {/* Active panel */}
      {active && (
        <div ref={triggerRef} className="scroll-mt-24" data-testid={`authority-panel-${active}`}>
          {active === "onboarding" && (
            <>
              <AmlTab caseId={caseId} caseData={caseData} externalAction={trigger} />
              {/* Live onboarding console — replaces the old "Phase 2"
                  coming-soon placeholder. Shows which of the 5 wizard
                  steps each client has completed (Payment · ID · CCL ·
                  Questionnaires · TA), with per-step download links,
                  nudge buttons and bounce flags. */}
              <div className="mt-4">
                <OnboardingStatusPanel caseId={caseId} />
              </div>
            </>
          )}
          {active === "hmlr" && (
            <SearchesTab caseId={caseId} caseData={caseData} isSolicitor={isSolicitor} externalAction={trigger} />
          )}
          {active === "post_completion" && (
            <PostCompletionTab caseId={caseId} caseData={caseData} isSolicitor={isSolicitor} externalAction={trigger} />
          )}
          {active === "legalito" && (
            <LegalitoPanel caseId={caseId} isSolicitor={isSolicitor} />
          )}
          <div className="text-right mt-3">
            <Button variant="ghost" size="sm" onClick={() => setActive(null)} data-testid="authority-collapse">
              Close panel
            </Button>
          </div>
        </div>
      )}

      <OnboardingPackDialog
        open={packOpen}
        onOpenChange={setPackOpen}
        caseId={caseId}
      />
      <ContractPackDialog
        open={contractOpen}
        onOpenChange={setContractOpen}
        caseId={caseId}
      />
    </div>
  );
}

// Minimal Fragment shim removed — we now import { Fragment } from "react"
// at the top of this file. Keeping the original tone-key + border-key
// pair explicit in TONE_CLASSES below also avoided a fragile string
// rewrite (`tone.ring.replace('ring-', 'border-')`) flagged in review.
